import * as React from 'react';
import type { CalendarCombinedProps } from './CalendarCombined';
import { Alert, Button, Modal, Row, Spinner, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BookingExtendedView, EventExtendedView, EventHostView } from 'cb-model';
import {
  CalendarView,
  ColourKey,
  Display,
  DisplayFieldBlock,
  selectUser,
  useCalendarRange,
  useCacheQuery,
  useCloneEntity,
} from 'gossamer-front-end';
import { useSelector } from 'react-redux';
import { getFilter } from 'gossamer-unified';
import { formatEventUrl } from 'shared-front-end';
import { UserBookingCalendarEvent, EVENT_TYPE_COLORS, EVENT_TYPE_LEGEND } from './CalendarCombined';
import { makeWebsiteEventQueryFilters } from '../website-events/WebsiteEventFilters';

type EventTypes = keyof typeof EVENT_TYPE_COLORS;

export const CalendarDesktop = (props: CalendarCombinedProps): React.JSX.Element => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { range } = useCalendarRange();
  const [selectedBooking, setSelectedBooking] = React.useState<UserBookingCalendarEvent | null>(null);
  const [activeColourKeyItems, setActiveColourKeyItems] = React.useState<EventTypes[]>(
    EVENT_TYPE_LEGEND.map((item) => item.id as EventTypes),
  );

  const LocalBookingExtendedView = useCloneEntity(BookingExtendedView, (local) => {
    Object.values(local.fields).forEach((field) => {
      field.visible = false;
    });
    local.fields.type.visible = true;
    local.fields.start_at.visible = true;
    local.fields.end_at.visible = true;
    local.fields.location_type.visible = true;
    local.fields.location.visible = true;
    local.fields.visibility_limit.visible = true;
    local.fields.visibility_limit.formColumnWidths = { md: 12 };
    local.fields.flow_option.visible = true;
    local.fields.flow_option.formColumnWidths = { md: 12 };
    local.fields.ticket_title.visible = true;
    local.fields.booked_quantity.visible = true;
    local.fields.computed_status.visible = true;
  });

  const eventsQuery = useCacheQuery(EventExtendedView, {
    where: {
      and: [
        getFilter('institution_id', 'eq', String(props.institutionId)),
        getFilter('type', 'ne', 'on-demand'),
        getFilter('end_at', 'gt', range.start.toISOString()),
        getFilter('start_at', 'lt', range.end.toISOString()),
        ...makeWebsiteEventQueryFilters(props.eventFilters, { includeFutureDefault: false }),
      ],
    },
    order: [['start_at', 'ASC']],
    limit: 1000,
  });

  const bookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('booking_status', 'eq', 'active'),
          getFilter('end_at', 'gt', range.start.toISOString()),
          getFilter('start_at', 'lt', range.end.toISOString()),
          ...makeWebsiteEventQueryFilters(props.eventFilters, {
            includeFutureDefault: false,
            includeVisibilityFilters: false,
            startFieldId: 'start_at',
          }),
        ],
      },
      order: [['start_at', 'ASC']],
      limit: 1000,
    },
    !user,
  );

  const events = (eventsQuery.data || [])
    .filter((event) => activeColourKeyItems.includes(event.visibility_limit.toLowerCase() as EventTypes))
    .map(
      (event) =>
        ({
          id: String(event.id),
          title: event.title,
          start: new Date(event.start_at),
          end: new Date(event.end_at),
          colorCode: EVENT_TYPE_COLORS[event.visibility_limit.toLowerCase()] || '#ACBDE9',
        }) as UserBookingCalendarEvent,
    )
    .concat(
      (bookingsQuery.data || [])
        .filter((_booking) => activeColourKeyItems.includes('booking'))
        .map((booking) => ({
          id: String(booking.event_id),
          title: booking.title,
          start: new Date(booking.start_at),
          end: new Date(booking.end_at),
          colorCode: EVENT_TYPE_COLORS['booking'],
          // hostedEvent,
        })),
    );

  return (
    <>
      {bookingsQuery.isFetching && <Spinner animation="border" />}
      {bookingsQuery.error && <Alert variant="danger">Unable to load bookings.</Alert>}
      <CalendarView<UserBookingCalendarEvent>
        events={events}
        handleEventSelect={(event) => setSelectedBooking(event)}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.colorCode,
            borderColor: event.colorCode,
            // color: getBookingEventTextColor(event.colorCode),
          },
        })}
      />
      <ColourKey
        items={EVENT_TYPE_LEGEND}
        activeItems={activeColourKeyItems}
        updateActiveItems={setActiveColourKeyItems}
      />
      <Modal
        show={!!selectedBooking}
        onHide={() => setSelectedBooking(null)}
        aria-labelledby="website-booking-calendar-modal-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="website-booking-calendar-modal-title" as="h5">
            {selectedBooking?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              {!!selectedBooking.booking && (
                <Display
                  entity={LocalBookingExtendedView}
                  keyValue={String(selectedBooking.booking.ticket_booking_id)}
                  row={selectedBooking.booking}
                />
              )}
              {!selectedBooking.booking && (
                <Row>
                  <DisplayFieldBlock
                    fieldId="start"
                    field={{ ...EventHostView.fields.start_at, formColumnWidths: { sm: 12 } }}
                    value={selectedBooking.start.toISOString()}
                  />
                  <DisplayFieldBlock
                    fieldId="end"
                    field={{ ...EventHostView.fields.end_at, formColumnWidths: { sm: 12 } }}
                    value={selectedBooking.end.toISOString()}
                  />
                </Row>
              )}
              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    navigate(formatEventUrl(selectedBooking.title as string, selectedBooking.id));
                  }}
                >
                  Go to Event
                </Button>
              </Stack>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
